import { Component, Input, OnInit } from '@angular/core';
import { IonicModule, ModalController, AlertController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Timestamp } from '@angular/fire/firestore';
import { PlaylistService } from '../../services/playlist.service';
import { SongService } from '../../services/song.service';
import { YouTubeImportService } from '../../services/youtube-import.service';
import { PlaylistVisibility, PlaylistCollaboration } from '../../entities/playlist.model';

@Component({
  selector: 'app-create-playlist-modal',
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, TranslateModule],
  templateUrl: './create-playlist-modal.component.html',
  styleUrls: ['./create-playlist-modal.component.scss'],
})
export class CreatePlaylistModalComponent implements OnInit {
  @Input() createdBy = '';
  @Input() createdByUid = '';
  @Input() language = '';

  // Edit mode inputs
  @Input() editMode = false;
  @Input() playlistId = '';
  @Input() initialName = '';
  @Input() initialDescription = '';
  @Input() initialVisibility: PlaylistVisibility = 'public';
  @Input() initialCollaboration: PlaylistCollaboration = 'editor';

  name = '';
  description = '';
  visibility: PlaylistVisibility = 'public';
  collaboration: PlaylistCollaboration = 'editor';
  submitting = false;
  nameError = false;

  // YouTube import fields (create mode only)
  youtubePlaylistUrl = '';
  youtubeUrlError = false;
  importStatus = '';   // shown during import progress

  get isPrivate(): boolean {
    return this.visibility === 'private';
  }

  get youtubeImportConfigured(): boolean {
    return this.youtubeImport.isConfigured();
  }

  constructor(
    private modalController: ModalController,
    private playlistService: PlaylistService,
    private songService: SongService,
    private youtubeImport: YouTubeImportService,
    private alertController: AlertController,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    if (this.editMode) {
      this.name = this.initialName;
      this.description = this.initialDescription;
      this.visibility = this.initialVisibility;
      this.collaboration = this.initialCollaboration;
    }
  }

  onYoutubeUrlChange() {
    if (this.youtubePlaylistUrl.trim()) {
      this.youtubeUrlError = !this.youtubeImport.isValidPlaylistUrl(this.youtubePlaylistUrl);
    } else {
      this.youtubeUrlError = false;
    }
  }

  async submit() {
    this.nameError = !this.name.trim();
    if (this.nameError) return;

    // Validate YouTube URL if provided
    const ytUrl = this.youtubePlaylistUrl.trim();
    if (ytUrl && !this.youtubeImport.isValidPlaylistUrl(ytUrl)) {
      this.youtubeUrlError = true;
      return;
    }

    this.submitting = true;
    this.importStatus = '';
    try {
      if (this.editMode && this.playlistId) {
        await this.playlistService.updatePlaylist(this.playlistId, {
          name: this.name.trim(),
          description: this.description.trim(),
          visibility: this.visibility,
          collaboration: this.collaboration,
        });
        this.modalController.dismiss({ submitted: true });
        return;
      }

      // Create the playlist
      let newPlaylistId: string;
      try {
        newPlaylistId = await this.playlistService.addPlaylist({
          name: this.name.trim(),
          description: this.description.trim(),
          language: this.language,
          createdBy: this.createdBy,
          createdByUid: this.createdByUid || undefined,
          createdAt: Timestamp.now(),
          totalLikes: 0,
          songCount: 0,
          visibility: this.visibility,
          collaboration: this.collaboration,
        });
      } catch (error) {
        console.error('Error creating playlist:', error);
        this.importStatus = '';
        this.submitting = false;
        return;
      }

      // Import YouTube songs if a URL was provided (failure here still closes the modal)
      if (ytUrl && newPlaylistId) {
        try {
          await this.importYouTubeSongs(ytUrl, newPlaylistId);
        } catch (error: any) {
          console.error('YouTube import error:', error);
          this.importStatus = '';
          this.modalController.dismiss({ submitted: true });
          const alert = await this.alertController.create({
            header: this.translate.instant('PLAYLISTS.IMPORT_ERROR_TITLE'),
            message: `${this.translate.instant('PLAYLISTS.IMPORT_ERROR_MSG')} (${error?.message ?? 'unknown error'})`,
            buttons: [this.translate.instant('SETTINGS.OK')]
          });
          await alert.present();
          this.submitting = false;
          return;
        }
      }

      this.modalController.dismiss({ submitted: true });
    } catch (error) {
      console.error('Error saving playlist:', error);
      this.importStatus = '';
    } finally {
      this.submitting = false;
    }
  }

  private async importYouTubeSongs(url: string, playlistId: string): Promise<void> {
    const playlistIdYt = this.youtubeImport.extractPlaylistId(url)!;
    this.importStatus = this.translate.instant('PLAYLISTS.IMPORT_FETCHING');

    const videos = await this.youtubeImport.fetchPlaylistVideos(playlistIdYt);
    const capped = videos.slice(0, 100);

    for (let i = 0; i < capped.length; i++) {
      const v = capped[i];
      this.importStatus = this.translate.instant('PLAYLISTS.IMPORT_PROGRESS', {
        current: i + 1,
        total: capped.length
      });
      await this.songService.addSong({
        language: this.language,
        topic: playlistId,
        youtubeUrl: v.youtubeUrl,
        title: v.title,
        channelName: v.channelName,
        thumbnailUrl: v.thumbnailUrl,
        note: '',
        upvotes: 0,
        downvotes: 0,
        submittedBy: this.createdBy,
        submittedByUid: this.createdByUid || undefined,
        createdAt: Timestamp.now(),
      });
    }

    // Update the song count on the playlist
    for (let i = 0; i < capped.length; i++) {
      this.playlistService.incrementSongCount(playlistId).catch(console.error);
    }
  }

  async confirmDelete() {
    const alert = await this.alertController.create({
      header: this.translate.instant('PLAYLISTS.MODAL_DELETE_TITLE'),
      message: this.translate.instant('PLAYLISTS.MODAL_DELETE_MSG', { name: this.name }),
      buttons: [
        { text: this.translate.instant('PLAYLISTS.CANCEL'), role: 'cancel' },
        {
          text: this.translate.instant('PLAYLISTS.MODAL_DELETE_BTN'),
          role: 'destructive',
          handler: async () => {
            try {
              await this.playlistService.deletePlaylist(this.playlistId);
              this.modalController.dismiss({ deleted: true });
            } catch (error) {
              console.error('Error deleting playlist:', error);
            }
          }
        }
      ]
    });
    await alert.present();
  }

  dismiss() {
    this.modalController.dismiss();
  }
}
